import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { CoverPageIcon } from '@pleio/icons/cover-page'
import { InfoIcon } from '@pleio/icons/info'

import Spacer from 'js/components/Spacer/Spacer'
import HeaderLogo from 'js/magazine/components/HeaderLogo/HeaderLogo'
import TableOfContentsItem from 'js/magazine-issue/View/components/TableOfContents/TableOfContentsItem'
import type { MagazineIssueViewEntity } from 'js/magazine-issue/View/View.types'

import styles from './TableOfContents.module.css'

export const getPageOrder = (entity: MagazineIssueViewEntity): string[] => {
    const { url, articles, colophon } = entity
    const hasColophon = colophon.some((c) => c.value)
    return [
        url,
        ...articles.map((a) => a.url),
        ...(hasColophon ? [`${url}/colophon`] : []),
    ]
}

type TableOfContentsProps = {
    entity: MagazineIssueViewEntity
}

const TableOfContents = ({ entity }: TableOfContentsProps) => {
    const { t } = useTranslation()
    const location = useLocation()

    const { url, issueNumber, timePublished, container, articles, colophon } =
        entity

    const hasColophon = colophon.some((c) => c.value)
    const pageOrder = getPageOrder(entity)
    const currentIndex = pageOrder.indexOf(location.pathname)

    const getDirection = (targetUrl: string): 'back' | 'forward' => {
        const targetIndex = pageOrder.indexOf(targetUrl)
        return targetIndex > currentIndex ? 'forward' : 'back'
    }

    return (
        <div className={styles.wrapper}>
            <HeaderLogo
                icon={container.icon}
                title={container.title}
                number={issueNumber}
                subtitle={container.subtitle}
                date={timePublished}
                isSmall
            />
            <Spacer spacing="small" as="ul" className={styles.tocList}>
                <li>
                    <TableOfContentsItem
                        url={url}
                        title={t('entity-magazine.cover-page')}
                        icon={<CoverPageIcon />}
                        direction={getDirection(url)}
                    />
                </li>
                {articles.map(
                    ({ guid, url: articleUrl, featured, title, abstract }) => (
                        <li key={guid}>
                            <TableOfContentsItem
                                url={articleUrl}
                                title={title}
                                abstract={abstract}
                                featured={featured}
                                direction={getDirection(articleUrl)}
                            />
                        </li>
                    ),
                )}
                {hasColophon && (
                    <li>
                        <TableOfContentsItem
                            url={`${url}/colophon`}
                            title={t('entity-magazine.colophon')}
                            icon={<InfoIcon />}
                            direction={getDirection(`${url}/colophon`)}
                        />
                    </li>
                )}
            </Spacer>
        </div>
    )
}

export default TableOfContents
